<template>
  <v-container fluid class="pa-0">
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="6">
        <h2 class="text-h4 white--text font-weight-bold">Lead Statistiken</h2>
        <div class="text-subtitle-1 grey--text">Performance Übersicht deiner Studios und Kampagnen</div>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end align-center">
        <v-menu offset-y :close-on-content-click="false" content-class="rounded-lg">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary" outlined v-bind="attrs" v-on="on" class="mr-2"> <v-icon left>mdi-filter-variant</v-icon> Filter </v-btn>
          </template>
          <v-card min-width="300" class="pa-4" color="#1E1E1E" dark>
            <v-subheader class="white--text pl-0">Kampagnen filtern</v-subheader>
            <v-select
              dense
              outlined
              hide-details
              multiple
              dark
              label="Kampagnen auswählen"
              :items="availableCampaignKeys"
              v-model="leadsPerSettingSelection"
              @change="handleFilterChange"
              menu-props="{ contentClass: 'dark-menu' }">
              <template v-slot:prepend-item>
                <v-list-item ripple @click="toggleAllCampaigns">
                  <v-list-item-content>
                    <v-list-item-title>Alle auswählen</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
            </v-select>

            <v-btn-toggle v-model="quickFilter" class="mt-4 d-block" dense dark>
              <v-btn value="referral" small block @click="applyQuickFilter('referral')">Weiterempfehlungen1</v-btn>

              <v-btn value="10days" small block @click="applyQuickFilter('10days')">10-Tage Leads</v-btn>

              <v-btn value="11days" small block @click="applyQuickFilter('11days')">11-Tage Kampagne</v-btn>
            </v-btn-toggle>
          </v-card>
        </v-menu>

        <v-menu offset-y content-class="rounded-lg">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="#00E396" dark v-bind="attrs" v-on="on"> <v-icon left>mdi-download</v-icon> Export </v-btn>
          </template>
          <v-list color="#1E1E1E" dark>
            <v-list-item @click="downloadExcel">
              <v-list-item-icon><v-icon color="#00E396">mdi-file-excel</v-icon></v-list-item-icon>
              <v-list-item-title>Alle Leads (Excel)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadLeadCostExcel">
              <v-list-item-icon><v-icon color="#00E396">mdi-currency-eur</v-icon></v-list-item-icon>
              <v-list-item-title>Kostenanalyse (Excel)</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <v-row class="mb-2">
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-text class="d-flex justify-space-between align-start">
            <div>
              <div class="text-overline mb-1 grey--text">Leads (Auswahl)</div>
              <div class="text-h4 font-weight-bold white--text">{{ kpiTotalLeads }}</div>
            </div>
            <v-avatar color="primary" rounded size="48" class="elevation-3">
              <v-icon color="white">mdi-account-group</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-text class="d-flex justify-space-between align-start">
            <div>
              <div class="text-overline mb-1 grey--text">Top Quelle</div>
              <div class="text-h5 font-weight-bold white--text text-truncate" style="max-width: 150px" :title="kpiTopSource">
                {{ kpiTopSource }}
              </div>
            </div>
            <v-avatar color="#00E396" rounded size="48" class="elevation-3">
              <v-icon color="white">mdi-trophy</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-text class="d-flex justify-space-between align-start">
            <div>
              <div class="text-overline mb-1 grey--text">Top Studio</div>
              <div class="text-h5 font-weight-bold white--text text-truncate" style="max-width: 150px" :title="kpiTopStudio">
                {{ kpiTopStudio }}
              </div>
            </div>
            <v-avatar color="#008FFB" rounded size="48" class="elevation-3">
              <v-icon color="white">mdi-map-marker-star</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-text class="d-flex justify-space-between align-start">
            <div>
              <div class="text-overline mb-1 grey--text">Conversion Rate</div>
              <div class="text-h4 font-weight-bold white--text">{{ kpiConversion }}%</div>
            </div>
            <v-avatar color="#FEB019" rounded size="48" class="elevation-3">
              <v-icon color="white">mdi-chart-line</v-icon>
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card class="rounded-lg fill-height" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="d-flex justify-space-between white--text">
            <span>Leads nach Kampagne</span>
          </v-card-title>
          <v-card-text>
            <div v-if="seriesPerSetting.length > 0">
              <apexchart type="bar" height="350" :options="chartOptionsStacked" :series="seriesPerSetting"></apexchart>
            </div>
            <div v-else class="text-center py-10 grey--text">Keine Daten vorhanden</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="rounded-lg fill-height" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text">Status Verteilung</v-card-title>
          <v-card-text class="d-flex align-center justify-center" style="height: 350px">
            <div v-if="seriesStatus.length > 0" style="width: 100%">
              <apexchart type="donut" :options="chartOptionsPie" :series="seriesStatus"></apexchart>
            </div>
            <div v-else class="text-center grey--text">Keine Daten vorhanden</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="seriesEfficiency.length > 0 && seriesEfficiency[0].data.length > 1">
      <v-col cols="12">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text">
            Effizienz-Ranking: Conversion Rate pro Kampagne
            <v-spacer></v-spacer>
            <span class="text-caption grey--text">Verhältnis Verträge / Total Leads</span>
          </v-card-title>
          <v-card-text>
            <apexchart type="bar" height="250" :options="chartOptionsEfficiency" :series="seriesEfficiency"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-toolbar flat color="transparent" dense class="mb-2">
            <v-toolbar-title class="white--text text-subtitle-1">Verlauf über Zeit</v-toolbar-title>
            <v-spacer></v-spacer>

            <v-btn-toggle v-model="timeChartMode" mandatory dense rounded color="primary">
              <v-btn value="total" small> <v-icon left small>mdi-chart-timeline-variant</v-icon> Gesamt </v-btn>
              <v-btn value="split" small> <v-icon left small>mdi-chart-line</v-icon> Detailliert </v-btn>
            </v-btn-toggle>
          </v-toolbar>

          <v-card-text>
            <div v-if="seriesTime.length > 0">
              <apexchart
                :type="timeChartMode === 'split' ? 'line' : 'area'"
                height="350"
                :options="chartOptionsArea"
                :series="seriesTime"></apexchart>
            </div>
            <div v-else class="text-center py-5 grey--text">Keine Zeitdaten für die aktuelle Auswahl verfügbar</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-toolbar flat color="transparent" dark>
            <v-toolbar-title class="white--text">Detaillierte Auswertung</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewMode" mandatory dense rounded color="primary" class="mr-2">
              <v-btn value="table" small> <v-icon small left>mdi-table</v-icon> Tabelle </v-btn>
              <v-btn value="chart" small> <v-icon small left>mdi-chart-bar</v-icon> Studio Chart </v-btn>
            </v-btn-toggle>
          </v-toolbar>

          <v-divider class="grey darken-3"></v-divider>

          <v-card-text>
            <div v-if="viewMode === 'table'">
              <v-text-field
                v-model="tableSearch"
                append-icon="mdi-magnify"
                label="Suchen (Studio, Kampagne...)"
                single-line
                hide-details
                outlined
                dense
                dark
                color="white"
                class="mb-4"
                style="max-width: 300px"></v-text-field>

              <v-data-table
                :headers="tableHeaders"
                :items="tableItems"
                :search="tableSearch"
                :items-per-page="15"
                class="elevation-0 transparent-table"
                dark
                dense>
                <template v-slot:item="{ item, headers }">
                  <tr>
                    <td
                      v-for="(header, index) in headers"
                      :key="index"
                      :class="{
                        'font-weight-bold': header.value === 'total' || header.value === 'name' || header.value === 'campaign',
                      }">
                      <span v-if="header.value === 'name'" class="white--text">{{ item.name }}</span>
                      <span v-else-if="header.value === 'campaign'" class="primary--text">{{ item.campaign }}</span>
                      <span v-else>
                        <span :class="item[header.value] > 0 ? 'white--text' : 'grey--text text--darken-2'">
                          {{ item[header.value] || 0 }}
                        </span>
                      </span>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>

            <div v-else>
              <apexchart
                v-if="seriesPerStudio.length > 0"
                type="bar"
                height="500"
                :options="chartOptionsStudio"
                :series="seriesPerStudio"></apexchart>
              <div v-else class="text-center py-5 grey--text">Keine Studio-Daten verfügbar</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "@/api"

const STATUS_COLORS = [
  "#1abc9c", // Türkis (Wurde angeworben)
  "#2ecc71", // Smaragd (Vertrag abgeschlossen)
  "#3498db", // Blau (Probetraining)
  "#9b59b6", // Lila
  "#34495e", // Dunkelblau (Kontaktiert)
  "#f1c40f", // Gelb
  "#e67e22", // Orange
  "#e74c3c", // Rot (Kein Abschluss)
]

export default {
  name: "LeadStatisticsDashboard",
  props: {
    datePickerRange: {
      type: Array,
      default: () => [],
    },
    studios: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isUpdatingData: false, // ✨ NEU: Unser Schutzschild
      dataKey: 0,
      viewMode: "table",
      quickFilter: null,
      tableSearch: "",
      timeChartMode: "total",
      statisticInfo: {
        leadsPerFormAndStudioTable: {},
        leadsPerStudio: {},
        leadsPerStatus: [],
        leadsPerSetting: {},
        leadsPerSettingAndTime: [],
        leadsPerTime: [],
        referralLeadsCount: [],
      },
      statisticInfoBackup: {},

      leadsPerSettingSelection: ["Alle"],
    }
  },
  computed: {
    // --- DYNAMIC KPIS BASED ON FILTER ---
    kpiTotalLeads() {
      // Calculate sum from filtered leadsPerSetting
      const settings = this.statisticInfo.leadsPerSetting || {}
      let grandTotal = 0
      for (const key in settings) {
        grandTotal += Object.values(settings[key]).reduce((a, b) => a + b, 0)
      }
      return grandTotal
    },
    kpiTopSource() {
      if (!this.statisticInfo.leadsPerSetting) return "-"
      let max = 0
      let topKey = "-"
      const settings = this.statisticInfo.leadsPerSetting
      for (const key in settings) {
        let total = Object.values(settings[key]).reduce((a, b) => a + b, 0)
        if (total > max) {
          max = total
          topKey = key
        }
      }
      return topKey.replace("Weiterempfehlung ", "").replace("St. Gallen ", "")
    },
    kpiTopStudio() {
      // Calculate Top Studio from filtered leadsPerFormAndStudioTable
      const raw = this.statisticInfo.leadsPerFormAndStudioTable || {}
      const studioTotals = {}

      for (const camp in raw) {
        for (const studio in raw[camp]) {
          if (studio === "Gesamt") continue
          if (!studioTotals[studio]) studioTotals[studio] = 0
          const sum = Object.values(raw[camp][studio]).reduce((a, b) => a + b, 0)
          studioTotals[studio] += sum
        }
      }

      // Find max key
      let max = 0
      let topStudio = "-"
      for (const s in studioTotals) {
        if (studioTotals[s] > max) {
          max = studioTotals[s]
          topStudio = s
        }
      }
      return topStudio
    },
    kpiConversion() {
      const total = this.kpiTotalLeads
      if (total === 0) return 0

      // Calculate 'Vertrag abgeschlossen' directly from filtered settings
      let soldCount = 0
      const settings = this.statisticInfo.leadsPerSetting || {}
      for (const key in settings) {
        // Check if the status key exists (often slightly different names, assuming standard)
        // Based on your JSON: "Vertrag abgeschlossen"
        if (settings[key]["Vertrag abgeschlossen"]) {
          soldCount += settings[key]["Vertrag abgeschlossen"]
        }
      }

      return ((soldCount / total) * 100).toFixed(1)
    },

    availableCampaignKeys() {
      if (!this.statisticInfoBackup.leadsPerSetting) return []
      return ["Alle", ...Object.keys(this.statisticInfoBackup.leadsPerSetting)]
    },

    // --- CHART 1: STACKED BAR (SETTINGS) ---
    seriesPerSetting() {
      const dataObj = this.statisticInfo.leadsPerSetting
      if (!dataObj || Object.keys(dataObj).length === 0) return []

      const statusSet = new Set()
      const settings = Object.keys(dataObj)

      settings.forEach((setting) => {
        Object.keys(dataObj[setting]).forEach((status) => statusSet.add(status))
      })

      // Sort settings by total volume
      const sortedSettings = settings.sort((a, b) => {
        const totalA = Object.values(dataObj[a]).reduce((sum, val) => sum + val, 0)
        const totalB = Object.values(dataObj[b]).reduce((sum, val) => sum + val, 0)
        return totalB - totalA
      })

      const series = []
      Array.from(statusSet).forEach((status) => {
        const data = sortedSettings.map((setting) => dataObj[setting][status] || 0)
        series.push({ name: status, data })
      })

      return series
    },

    chartOptionsStacked() {
      const dataObj = this.statisticInfo.leadsPerSetting || {}
      const sortedSettings = Object.keys(dataObj).sort((a, b) => {
        const totalA = Object.values(dataObj[a]).reduce((sum, val) => sum + val, 0)
        const totalB = Object.values(dataObj[b]).reduce((sum, val) => sum + val, 0)
        return totalB - totalA
      })

      const categories = sortedSettings.map((e) => e.replace("Weiterempfehlung ", "").replace("St. Gallen ", ""))

      return {
        chart: {
          stacked: true,
          toolbar: { show: false },
          background: "transparent",
          foreColor: "#ffffff",
        },
        theme: { mode: "dark" },
        plotOptions: { bar: { horizontal: false, borderRadius: 2 } },
        dataLabels: { enabled: false },
        colors: STATUS_COLORS,
        xaxis: {
          categories,
          labels: { style: { colors: "#ffffff" }, rotate: -45, trim: true },
        },
        yaxis: { labels: { style: { colors: "#ffffff" } } },
        legend: { position: "top", labels: { colors: "#ffffff" } },
        tooltip: { theme: "dark" },
        grid: { borderColor: "#333" },
      }
    },

    // --- CHART 2: DONUT (STATUS - DYNAMIC) ---
    seriesStatus() {
      // Recalculate totals per status based on filtered settings
      const settings = this.statisticInfo.leadsPerSetting || {}
      const statusMap = {}

      for (const camp in settings) {
        for (const stat in settings[camp]) {
          if (!statusMap[stat]) statusMap[stat] = 0
          statusMap[stat] += settings[camp][stat]
        }
      }
      // To keep consistent order/colors, we might want to sort or map to known statuses
      // For simpler array return:
      return Object.values(statusMap)
    },
    chartOptionsPie() {
      // Need keys matching seriesStatus
      const settings = this.statisticInfo.leadsPerSetting || {}
      const statusMap = {}
      for (const camp in settings) {
        for (const stat in settings[camp]) {
          if (!statusMap[stat]) statusMap[stat] = 0
          statusMap[stat] += settings[camp][stat]
        }
      }

      return {
        labels: Object.keys(statusMap),
        colors: STATUS_COLORS,
        chart: { background: "transparent", foreColor: "#ffffff" },
        theme: { mode: "dark" },
        legend: { position: "bottom", labels: { colors: "#ffffff" } },
        dataLabels: { enabled: true, style: { colors: ["#fff"] } },
        stroke: { show: false },
        tooltip: { theme: "dark" },
      }
    },

    // --- CHART 3: EFFICIENCY (NEW) ---
    seriesEfficiency() {
      const settings = this.statisticInfo.leadsPerSetting || {}
      const data = []

      const sortedKeys = Object.keys(settings).sort((a, b) => {
        // Sort by conversion rate desc
        const getRate = (k) => {
          const sold = settings[k]["Vertrag abgeschlossen"] || 0
          const total = Object.values(settings[k]).reduce((s, v) => s + v, 0)
          return total > 0 ? sold / total : 0
        }
        return getRate(b) - getRate(a)
      })

      sortedKeys.forEach((key) => {
        const total = Object.values(settings[key]).reduce((s, v) => s + v, 0)
        // Only show if relevant traffic (> 2 leads)
        if (total > 2) {
          const sold = settings[key]["Vertrag abgeschlossen"] || 0
          const rate = ((sold / total) * 100).toFixed(1)
          data.push({
            x: key.replace("Weiterempfehlung ", "").replace("St. Gallen ", ""),
            y: parseFloat(rate),
          })
        }
      })

      return [{ name: "Abschlussquote %", data: data }]
    },
    chartOptionsEfficiency() {
      return {
        chart: { type: "bar", background: "transparent", toolbar: { show: false } },
        theme: { mode: "dark" },
        plotOptions: {
          bar: { borderRadius: 4, dataLabels: { position: "top" } },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%"
          },
          offsetY: -20,
          style: { fontSize: "12px", colors: ["#fff"] },
        },
        colors: ["#00E396"], // Green for efficiency
        xaxis: {
          labels: { style: { colors: "#fff" }, rotate: -45, trim: true },
        },
        yaxis: {
          labels: { style: { colors: "#fff" } },
          title: { text: "Conversion Rate %", style: { color: "#fff" } },
        },
        grid: { borderColor: "#333" },
        tooltip: { theme: "dark" },
      }
    },

    // --- CHART 4: AREA (TIME) ---
    seriesTime() {
      // Fall 1: Benutzer will nur die Gesamtsumme sehen
      if (this.timeChartMode === "total") {
        if (!this.statisticInfo.leadsPerTime) return []
        return [
          {
            name: "Total Leads",
            type: "area", // Fläche gefüllt
            data: this.statisticInfo.leadsPerTime.map((item) => ({
              x: item.name,
              y: item.data,
            })),
          },
        ]
      }

      // Fall 2: Detaillierte Ansicht (Aufgeschlüsselt nach Kampagne)
      // Wir nutzen leadsPerSettingAndTime (Muss vom Backend kommen!)
      const rawData = this.statisticInfo.leadsPerSettingAndTime || []

      if (rawData.length === 0) return []

      // Filtern basierend auf der Auswahl oben im Dropdown
      let filteredSeries = []

      const selectedKeys = this.leadsPerSettingSelection
      const isAllSelected = selectedKeys.includes("Alle")

      if (isAllSelected) {
        // Wenn "Alle", zeigen wir alle verfügbaren Zeitreihen an
        // Optional: Begrenzen auf Top 10 um Performance zu sparen, falls es sehr viele sind
        filteredSeries = rawData
      } else {
        // Nur die ausgewählten Kampagnen anzeigen
        filteredSeries = rawData.filter((series) => selectedKeys.includes(series.name))
      }

      // Formatierung für ApexCharts sicherstellen
      return filteredSeries.map((series) => ({
        name: series.name.replace("Weiterempfehlung ", "").replace("St. Gallen ", ""), // Namen bereinigen für Legende
        type: "line", // Linien für bessere Vergleichbarkeit
        data: series.data, // Erwartet Format [[timestamp, value], ...] oder [{x, y}]
      }))
    },
    chartOptionsArea() {
      const isSplit = this.timeChartMode === "split"

      return {
        chart: {
          id: "time-chart",
          type: isSplit ? "line" : "area", // Linie bei Split, Fläche bei Total
          toolbar: { show: true, tools: { download: true, selection: true, zoom: true, pan: true } },
          background: "transparent",
          foreColor: "#ffffff",
          stacked: false, // Nicht stapeln, damit man den direkten Vergleich sieht
        },
        theme: {
          mode: "dark",
          palette: "palette1", // Automatische Farbpalette für viele Linien nutzen
        },
        dataLabels: { enabled: false },
        stroke: {
          curve: "smooth",
          width: isSplit ? 3 : 2,
        },
        xaxis: {
          type: "datetime",
          labels: { style: { colors: "#ffffff" }, datetimeFormatter: { year: "yyyy", month: "MMM", day: "dd.MM" } },
          axisBorder: { color: "#333" },
          axisTicks: { color: "#333" },
          tooltip: { enabled: false }, // Deaktiviert den X-Achsen Tooltip, stört oft bei vielen Linien
        },
        yaxis: {
          labels: { style: { colors: "#ffffff" } },
          title: { text: "Anzahl Leads", style: { color: "#888" } },
        },
        // Wenn Split: Farben automatisch (Palette). Wenn Total: Grün.
        colors: isSplit ? undefined : ["#00E396"],
        fill: {
          type: isSplit ? "solid" : "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1, stops: [0, 90, 100] },
        },
        grid: { borderColor: "#333", strokeDashArray: 3 },
        legend: {
          show: true, // Legende anzeigen bei Split
          position: "top",
          horizontalAlign: "left",
          labels: { colors: "#ffffff" },
        },
        tooltip: {
          theme: "dark",
          x: { format: "dd.MM.yyyy" },
          y: { formatter: (val) => val.toFixed(0) },
        },
      }
    },

    // --- CHART 5: BAR (STUDIO) ---
    seriesPerStudio() {
      // We calculate this from the filtered table data to respect the filter
      const raw = this.statisticInfo.leadsPerFormAndStudioTable || {}
      const studioTotals = {}

      for (const camp in raw) {
        for (const studio in raw[camp]) {
          if (studio === "Gesamt") continue
          if (!studioTotals[studio]) studioTotals[studio] = 0
          const sum = Object.values(raw[camp][studio]).reduce((a, b) => a + b, 0)
          studioTotals[studio] += sum
        }
      }

      const sortedData = Object.entries(studioTotals)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)

      return [
        {
          name: "Leads",
          data: sortedData.map((s) => s.total),
        },
      ]
    },
    chartOptionsStudio() {
      // Need categories matching seriesPerStudio sorting
      const raw = this.statisticInfo.leadsPerFormAndStudioTable || {}
      const studioTotals = {}
      for (const camp in raw) {
        for (const studio in raw[camp]) {
          if (studio === "Gesamt") continue
          if (!studioTotals[studio]) studioTotals[studio] = 0
          const sum = Object.values(raw[camp][studio]).reduce((a, b) => a + b, 0)
          studioTotals[studio] += sum
        }
      }

      const sortedCategories = Object.entries(studioTotals)
        .sort((a, b) => b[1] - a[1])
        .map((s) => s[0])

      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          background: "transparent",
          foreColor: "#ffffff",
        },
        theme: { mode: "dark" },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: "60%",
            distributed: true,
          },
        },
        colors: ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0"],
        xaxis: { categories: sortedCategories, labels: { style: { colors: "#ffffff" } } },
        yaxis: { labels: { style: { colors: "#ffffff" } } },
        tooltip: { theme: "dark" },
        grid: { borderColor: "#333" },
        legend: { show: false },
      }
    },

    // --- TABLE DATA ---
    tableHeaders() {
      const baseHeaders = [
        { text: "Kampagne", value: "campaign", align: "start" },
        { text: "Studio", value: "name", align: "start" },
      ]

      const statusSet = new Set()
      const rawData = this.statisticInfo.leadsPerFormAndStudioTable || {}

      for (const campaign in rawData) {
        for (const studio in rawData[campaign]) {
          if (studio === "Gesamt") continue
          for (const status in rawData[campaign][studio]) {
            statusSet.add(status)
          }
        }
      }

      const statusHeaders = Array.from(statusSet)
        .sort()
        .map((s) => ({
          text: s,
          value: s,
          align: "center",
        }))

      return [...baseHeaders, ...statusHeaders, { text: "Gesamt", value: "total", align: "center", sortable: true }]
    },

    tableItems() {
      const items = []
      const rawData = this.statisticInfo.leadsPerFormAndStudioTable || {}

      for (const campaignName in rawData) {
        const studioData = rawData[campaignName]

        for (const studioName in studioData) {
          if (studioName === "Gesamt") continue

          const statusCounts = studioData[studioName]
          let rowTotal = 0

          const row = {
            campaign: campaignName,
            name: studioName,
          }

          for (const status in statusCounts) {
            row[status] = statusCounts[status]
            rowTotal += statusCounts[status]
          }
          row.total = rowTotal
          items.push(row)
        }
      }
      return items
    },
  },
  created() {
    if (this.datePickerRange.length === 2) {
      this.fetchLeadStatistics()
    }
  },
  methods: {
    async fetchLeadStatistics() {
      if (this.datePickerRange.length !== 2) return // Sicherheits-Check

      try {
        const studioIds = this.studios.map((s) => s._id)

        this.isUpdatingData = true // ✨ SCHUTZ AKTIVIEREN

        const response = await api.getLeadStatistics({
          start_date: this.datePickerRange[0],
          end_date: this.datePickerRange[1],
          studios: studioIds,
        })

        // Backup aktualisieren
        this.statisticInfoBackup = JSON.parse(JSON.stringify(response))

        // ✨ NEU: Filter direkt nach dem Laden neu anwenden, falls aktiv
        if (this.quickFilter) {
          // Wendet den aktuellen Quick Filter (z.B. 'referral') auf die NEUEN Daten an
          this.applyQuickFilter(this.quickFilter)
        } else {
          // Falls kein Quick-Filter aktiv ist, den normalen Dropdown-Status anwenden
          this.filterData()
        }

        this.dataKey++

        // ✨ SCHUTZ DEAKTIVIEREN (einen Tick später, damit das UI fertig rendern kann)
        this.$nextTick(() => {
          this.isUpdatingData = false
        })
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Laden der Statistiken")
        this.isUpdatingData = false
      }
    },

    filterData() {
      const backup = this.statisticInfoBackup
      if (!backup.leadsPerSetting) return

      const selection = this.leadsPerSettingSelection
      const isAll = selection.includes("Alle")

      if (isAll) {
        this.statisticInfo = JSON.parse(JSON.stringify(backup))
        return
      }

      // Filter leadsPerSetting (Object)
      const filteredSettings = {}
      Object.keys(backup.leadsPerSetting).forEach((key) => {
        if (selection.includes(key)) {
          filteredSettings[key] = backup.leadsPerSetting[key]
        }
      })

      // Filter table data (Object)
      const filteredTable = {}
      Object.keys(backup.leadsPerFormAndStudioTable).forEach((key) => {
        if (selection.includes(key)) {
          filteredTable[key] = backup.leadsPerFormAndStudioTable[key]
        }
      })

      this.statisticInfo = {
        ...backup,
        leadsPerSetting: filteredSettings,
        leadsPerFormAndStudioTable: filteredTable,
      }
    },

    toggleAllCampaigns() {
      this.$nextTick(() => {
        if (this.leadsPerSettingSelection.includes("Alle")) {
          this.leadsPerSettingSelection = []
        } else {
          this.leadsPerSettingSelection = ["Alle", ...this.availableCampaignKeys.filter((k) => k !== "Alle")]
        }
        this.filterData()
      })
    },

    handleFilterChange(val) {
      // ✨ NEU: Wenn wir gerade neue Daten laden, ignoriere automatische Vuetify-Events!
      if (this.isUpdatingData) return

      this.quickFilter = null // Quick-Filter deaktivieren, wenn der Nutzer manuell das Dropdown nutzt

      if (val.length > 1 && val.includes("Alle") && val[val.length - 1] !== "Alle") {
        const index = this.leadsPerSettingSelection.indexOf("Alle")
        if (index !== -1) this.leadsPerSettingSelection.splice(index, 1)
      }
      if (val.length > 1 && val[val.length - 1] === "Alle") {
        this.leadsPerSettingSelection = ["Alle"]
      }
      if (val.length === 0) {
        this.leadsPerSettingSelection = ["Alle"]
      }
      this.filterData()
    },

    applyQuickFilter(type) {
      const allKeys = this.availableCampaignKeys
      let matches = []
      if (type === "referral") {
        matches = allKeys.filter((k) => k.toLowerCase().includes("weiterempfehlung"))
      } else if (type === "10days") {
        matches = allKeys.filter((k) => k.toLowerCase().includes("10 tage") || k.toLowerCase().includes("google"))
      } else if (type === "11days") {
        matches = allKeys.filter((k) => k.toLowerCase().includes("11 tage kampagne") || k.toLowerCase().includes("11 days"))
      }

      if (matches.length > 0) {
        this.leadsPerSettingSelection = matches
      } else {
        this.$toast.info("Keine Kampagnen für diesen Filter gefunden")
      }
      this.filterData()
    },

    async downloadLeadCostExcel() {
      try {
        await api.fetchLeadCostExcel()
        this.$toast.success("Download gestartet")
      } catch (e) {
        this.$toast.error("Fehler beim Download")
      }
    },
    async downloadExcel() {
      try {
        await api.fetchLeadExcel({
          start_date: this.datePickerRange[0],
          end_date: this.datePickerRange[1],
          studios: this.studios.map((studio) => studio._id),
        })
        this.$toast.success("Download gestartet")
      } catch (e) {
        this.$toast.error("Fehler beim Download")
      }
    },
  },
  watch: {
    datePickerRange: {
      handler() {
        if (this.datePickerRange.length === 2) {
          this.fetchLeadStatistics()
        }
      },
      deep: true,
    },
    studios: {
      handler() {
        this.fetchLeadStatistics()
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
.transparent-table {
  background-color: transparent !important;
}
::v-deep .v-data-table {
  background-color: transparent !important;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 4px;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #1e1e1e;
}
</style>
